import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dropdown, DropdownButton, Spinner } from 'react-bootstrap';

import { useGrades, useAlternativeGrades } from '../../../Utilities/useGrades';
import usePrivileges from '../../../Auth/usePrivileges';
import { LotDetailContext } from '../../../Contexts/LotDetailContext';

import LotSplitDialogSelector from '../../LotSplit/LotSplitDialogSelector';
import { useHttpUtils } from '../../../Utilities/useHttpUtils';
import useAlert from '../../../Alerts/useAlert';
import { useNavigate } from 'react-router';
import LotSplitPackageDialog, { LotSplitTypes } from '../../LotSplit/PackageSplit/LotSplitPackageDialog';
import LotSplitToMultipleLots from '../../LotSplit/PackageSplit/LotSplitToMultipleLots';
import LotSplitForQuality from '../../LotSplit/PackageSplit/LotSplitForQuality';

function SplitLotButton() {
    const { t } = useTranslation();
    const { fetchDataAuthenticated } = useHttpUtils();
    const { setAlert } = useAlert();
    const navigate = useNavigate();

    const { lotDetail: {
        summary: {
            lotNumber,
            businessUnitCode,
            currentStatus,
            processingStatus,
            grade,
            localBuildStart: buildStart,
            localBuildEnd: buildEnd,
            isSplitLotParent,
            isLocked },
        comments,
        packages },
        getLotDetail,
        lotFlags: {
            canLotSplit,
            canAutoSplit,
            canPackageQualitySplit
        } } = useContext(LotDetailContext);

    const { canReview } = usePrivileges(businessUnitCode);

    const [disabled, setDisabled] = useState<boolean>(false);
    const [showLotSplitDialog, setShowLotSplitDialog] = useState<boolean>(false);
    const [showLotSplitMultipleDialog, setShowLotSplitMultipleDialog] = useState<boolean>(false);
    const [showLotSplitForQualityDialog, setShowLotSplitForQualityDialog] = useState<boolean>(false);
    const { grades, loading: loadingGrades } = useGrades(businessUnitCode);
    const { alternativeGrades, loading: loadingAlternativeGrades } = useAlternativeGrades(lotNumber);

    useEffect(() => {
        switch (processingStatus) {
            case "Processing":
            case "Queued":
                setDisabled(true);
                break;
            default:
                switch (currentStatus) {
                    case "Graded":
                        setDisabled(true);
                        break;
                    default:
                        setDisabled(false);
                }
        }

        if (isLocked) {
            setDisabled(true);
        }
    }, [currentStatus, processingStatus, isLocked]);

    const lotContainsUnreliableBaggingData = () => {
        return comments.some(x => x.commentText.includes("Unreliable extruder times for loadout counts"));
    };

    const [loading, setLoading] = useState(false);
    const autoSplit = () => {
        setLoading(true);
        fetchDataAuthenticated(`api/v1/lot/${lotNumber}/autosplitpackages`, "PUT")
            .then(async (response: any) => {
                if (response.ok) {
                    setAlert("success", "Success", t("LotSplit.Successful"));
                    navigate('/lots');
                } else {
                    let errorMessage = await response.text();
                    throw new Error(`${t("LotSplit.Failed")} ${errorMessage}`);
                }
            })
            .catch((error: any) => {
                setAlert("danger", "Error", error.message);
            })
            .finally(() => setLoading(false));
    };

    const autoUndoSplit = () => {
        setLoading(true);
        fetchDataAuthenticated(`api/v1/lot/${lotNumber}/autoUndoSplitPackages`, "PUT")
            .then(async (response: any) => {
                if (response.ok) {
                    setAlert("success", "Success", t("LotSplit.Successful"));
                    getLotDetail(undefined, true);
                } else {
                    let errorMessage = await response.text();
                    throw new Error(`${t("LotSplit.Failed")} ${errorMessage}`);
                }
            })
            .catch((error: any) => {
                setAlert("danger", "Error", error.message);
            })
            .finally(() => setLoading(false));
    };

    const [preferredInitialLotDigit, setPreferredInitialLotDigit] = useState(5);
    const [splitType, setSplitType] = useState<LotSplitTypes>(LotSplitTypes.Quality);
    const [includeQualitiesSelection, setIncludeQualitiesSelection] = useState(false);
    const [showSplitTimes, setShowSplitTimes] = useState(true);

    const openSplitDialog = (splitType: LotSplitTypes) => {
        switch (splitType) {
            case LotSplitTypes.Quality:
                setIncludeQualitiesSelection(true);
                setPreferredInitialLotDigit(1);
                setShowSplitTimes(true);
                break;
            case LotSplitTypes.Proxy:
                switch (businessUnitCode) {
                    case "BTBU":
                        setPreferredInitialLotDigit(5);
                        break;
                    default:
                        setPreferredInitialLotDigit(6);
                }
                setIncludeQualitiesSelection(false);
                setShowSplitTimes(true);
                break;
            case LotSplitTypes.Create:
                setIncludeQualitiesSelection(false);
                setPreferredInitialLotDigit(0);
                setShowSplitTimes(true);
                break;
            case LotSplitTypes.Reassign:
                setIncludeQualitiesSelection(false);
                setPreferredInitialLotDigit(6);
                setShowSplitTimes(false);
                break;
            case LotSplitTypes.CreateMultiple:
                setShowLotSplitMultipleDialog(true);
                return; // Not used here
            case LotSplitTypes.QualityMultiple:
                setShowLotSplitForQualityDialog(true);
                return;
        }

        setSplitType(splitType);
        setShowLotSplitDialog(true);
    };

    if (businessUnitCode === "BRCP_VFU" || businessUnitCode === "BRCP_HFU") {
        const placeholderLot = lotNumber.slice(-3, -1) === "00";
        return (
            <>
                <DropdownButton
                    variant="primary"
                    disabled={disabled || !canReview}
                    hidden={currentStatus === "Graded" || !canLotSplit}
                    title={loading ? <Spinner size={'sm'} /> : t<string>('LotDetails.Summary.Split')}
                    className="d-flex"
                >
                    {canAutoSplit &&
                        <>
                            <Dropdown.Item key={`autosplit-split-option`} onClick={autoSplit}>{t<string>('LotDetails.Summary.AutoSplit')}</Dropdown.Item>
                            <Dropdown.Divider />
                        </>
                    }
                    {!placeholderLot &&
                        <>
                            {canPackageQualitySplit && <Dropdown.Item key={'quality-split-multiple-option'} onClick={() => openSplitDialog(LotSplitTypes.QualityMultiple)}>{t<string>('LotDetails.Summary.QualitySplitMultiple')}</Dropdown.Item>}
                            <Dropdown.Item key={'proxy-split-option'} onClick={() => openSplitDialog(LotSplitTypes.Proxy)}>{t<string>('LotDetails.Summary.ProxySplit')}</Dropdown.Item>
                        </>
                    }
                    <Dropdown.Item key={'create-many-lots-option'} onClick={() => openSplitDialog(LotSplitTypes.CreateMultiple)}>{t<string>('LotDetails.Summary.MultipleMainLotSplit')}</Dropdown.Item>
                    {!placeholderLot &&
                        <>
                            <Dropdown.Divider />
                            <Dropdown.Item key={'reassign-packages-option'} onClick={() => openSplitDialog(LotSplitTypes.Reassign)}>{t<string>('LotDetails.Summary.ReassignPackages')}</Dropdown.Item>
                            <Dropdown.Item key={'create-packages-option'} onClick={() => openSplitDialog(LotSplitTypes.Create)}>{t<string>('LotDetails.Summary.MainLotSplit')}</Dropdown.Item>
                            {canPackageQualitySplit && <Dropdown.Item key={'quality-split-option'} onClick={() => openSplitDialog(LotSplitTypes.Quality)}>{t<string>('LotDetails.Summary.QualitySplit')}</Dropdown.Item>}
                        </>
                    }
                    {!placeholderLot &&
                        <>
                            <Dropdown.Divider />
                            <Dropdown.Item key={`autoundosplit-split-option`} onClick={autoUndoSplit}>{t<string>('LotDetails.Summary.UndoSplit')}</Dropdown.Item>
                        </>
                    }
                </DropdownButton>
                <LotSplitPackageDialog
                    splitType={splitType}
                    showDialog={showLotSplitDialog}
                    closeDialog={() => setShowLotSplitDialog(false)}
                    alternativeGrades={alternativeGrades}
                    allGrades={grades}
                    includeQualitiesSelection={includeQualitiesSelection}
                    showSplitTimes={showSplitTimes}
                    preferredLotBDigit={preferredInitialLotDigit}
                    readonlySplitTimes={splitType === LotSplitTypes.Proxy}
                />
                <LotSplitToMultipleLots showDialog={showLotSplitMultipleDialog}
                    closeDialog={() => setShowLotSplitMultipleDialog(false)}
                />
                <LotSplitForQuality showDialog={
                    showLotSplitForQualityDialog}
                    closeDialog={() => setShowLotSplitForQualityDialog(false)}
                    alternativeGrades={alternativeGrades}
                />
            </>
        );
    }

    if (businessUnitCode === "BTBU") {
        return (
            <>
                <DropdownButton
                    variant="primary"
                    disabled={disabled || !canReview}
                    hidden={currentStatus === "Graded" || !canLotSplit}
                    title={loading ? <Spinner size={'sm'} /> : t<string>('LotDetails.Summary.Split')}
                    className="d-flex"
                >
                    <Dropdown.Item key={'proxy-split-option'} onClick={() => openSplitDialog(LotSplitTypes.Proxy)}>{t<string>('LotDetails.Summary.ProxySplit')}</Dropdown.Item>
                    <Dropdown.Item key={'quality-split-multiple-option'} onClick={() => openSplitDialog(LotSplitTypes.QualityMultiple)}>{t<string>('LotDetails.Summary.QualitySplitMultiple')}</Dropdown.Item>
                    <>
                        <Dropdown.Divider />
                        <Dropdown.Item key={`autoundosplit-split-option`} onClick={autoUndoSplit}>{t<string>('LotDetails.Summary.UndoSplit')}</Dropdown.Item>
                    </>
                </DropdownButton>
                <LotSplitPackageDialog
                    splitType={splitType}
                    showDialog={showLotSplitDialog}
                    closeDialog={() => setShowLotSplitDialog(false)}
                    alternativeGrades={alternativeGrades}
                    allGrades={grades}
                    includeQualitiesSelection={includeQualitiesSelection}
                    showSplitTimes={showSplitTimes}
                    preferredLotBDigit={preferredInitialLotDigit}
                    readonlySplitTimes={splitType === LotSplitTypes.Proxy}
                />
                <LotSplitToMultipleLots showDialog={showLotSplitMultipleDialog}
                    closeDialog={() => setShowLotSplitMultipleDialog(false)}
                />
                <LotSplitForQuality showDialog={
                    showLotSplitForQualityDialog}
                    closeDialog={() => setShowLotSplitForQualityDialog(false)}
                    alternativeGrades={alternativeGrades}
                />
            </>
        );
    }

    return (
        <>
            <Button
                variant="primary"
                onClick={() => setShowLotSplitDialog(true)}
                disabled={disabled || !canReview}
                hidden={isSplitLotParent || currentStatus === "Graded" || !canLotSplit} >
                {t('LotDetails.Summary.Split')}
            </Button>
            <LotSplitDialogSelector
                businessUnitCode={businessUnitCode}
                showDialog={showLotSplitDialog}
                closeDialog={() => setShowLotSplitDialog(false)}
                lotNumber={lotNumber}
                buildStart={buildStart}
                buildEnd={buildEnd}
                getLotDetail={getLotDetail}
                currentGrade={grade}
                alternativeGrades={alternativeGrades}
                allGrades={grades}
                loadingAlternativeGrades={loadingAlternativeGrades}
                loadingAllGrades={loadingGrades}
                unreliableBaggingData={lotContainsUnreliableBaggingData()}
                packages={packages}
            />
        </>
    );
}

export default SplitLotButton;
